import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { MOCK_ROUTES } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { AgedPackageStatusReport } from 'src/app/core/models/reports/reports.model';
import { OtherReportsService } from 'src/app/core/services/reports/other/other-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  AgedPackageStatusFormGroup,
  CONSTS,
  PDF_REPORT,
  REPORT_FILE_NAME,
} from './aged-package-status.constants';

@Component({
  selector: 'app-aged-package-status',
  templateUrl: './aged-package-status.component.html',
  styleUrls: ['./aged-package-status.component.scss'],
  standalone: false,
})
export class AgedPackageStatusComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: AgedPackageStatusFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<AgedPackageStatusComponent> = inject(
    MatDialogRef<AgedPackageStatusComponent>,
  );
  private otherReportsService: OtherReportsService =
    inject(OtherReportsService);
  private commonUtils: CommonUtils = inject(CommonUtils);
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  agedPackageStatusForm!: FormGroup;
  routes = MOCK_ROUTES;
  locationcode = '';

  agedPackageStatusReport!: AgedPackageStatusReport;
  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.locationcode = user?.location;
          return this.getRouteList();
        }),
      )
      .subscribe();

    this.agedPackageStatusForm = this.fb.group({
      routes: [MOCK_ROUTES[0]],
      reportFormat: [Options.PDF],
    });
  }

  onExit() {
    this.commonUtils.openExitModal(this.agedPackageStatusForm.dirty);
  }

  onAccept() {
    if (
      this.agedPackageStatusForm.get('routes')?.value.label === MOCK_ROUTES[0]
    ) {
      //Add new Routes (minus first entry) to API request
    }
    setTimeout(() => {
      this.dialogRef.close();
    }, 1000);
  }

  radioChange(newValue: Options) {
    this.agedPackageStatusForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to get route list
   *
   * @private
   * @memberof SendAgainComponent
   */
  private getRouteList() {
    return this.otherReportsService
      .getAgedPackageRoutes({ locationcode: this.locationcode })
      .pipe(
        take(1),
        map((data) => {
          this.routes = data.response.agedPkgRoutes
            .map(({ route }) => {
              return {
                label: route,
                value: route,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          this.routes.unshift({
            label: $localize`All`,
            value: 'All',
          });
        }),
      );
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof SendAgainComponent
   */
  getReport() {
    const formValue = this.agedPackageStatusForm.value;

    const request = {
      locationcode: this.locationcode,
      loop: formValue.routes?.value === 'All' ? 'All' : formValue.routes,
    };
    this.otherReportsService
      .getAgedPackageReport(request)
      .pipe(take(1))
      .subscribe((data) => {
        const formatType =
          this.agedPackageStatusForm.get('reportFormat')?.value;
        const agedPackageStatus = data.response.agedPackageStatus;
        if (agedPackageStatus?.length) {
          if (formatType === Options.CSV) {
            const customHeader = [
              ['', '', '', '', 'Aged Package Report', '', '', '', '', '', ''],
              [
                'LOOP:',
                `${data.response.loop}`,
                '',
                '',
                'LOCATION:',
                '',
                `${data.response.location}`,
                '',
                '',
                '',
                'Printed:',
                '',
                `${data.response.printed}`,
              ],
            ];
            this.reportUtils.exportCSVReport(
              agedPackageStatus,
              REPORT_FILE_NAME,
              customHeader,
            );
          }
          if (formatType === Options.PDF) {
            this.agedPackageStatusReport = data;
            setTimeout(() => {
              const content = this.pdfContainer.element.nativeElement.innerHTML;
              this.reportUtils.exportPdfReport(
                content,
                PDF_REPORT.NAME,
                REPORT_FILE_NAME,
                'l',
              );
            }, 0);
          }
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
        this.dialogRef.close();
      });
  }
}
